/**
 * api/device.ts —— 设备能力（真实硬件调用，D5 无 mock）
 *
 * 三条真实约束，每一条都在错误信息里如实告诉用户：
 *   ① getUserMedia 需要**安全上下文**（https 或 localhost）。file:// 直接不可用。
 *   ② 用户可能拒绝授权 —— 这不是 bug，是权利。错误文案要尊重这一点。
 *   ③ 设备可能根本不存在（台式机没摄像头）—— 与"拒绝"是不同的情况，要分开报。
 *
 * 这一层只做采集与释放，不碰 UI。摄像头预览的画面由覆盖层组件订阅
 * `device:camera-changed` 事件后自行拉流渲染 —— 保持"API 不改 UI"的分层。
 */

import { deviceRuntime } from '../device-runtime';
import { handles } from '../handles';
import { RuntimeError, type ObjectDef } from '../registry';
import { eventBus } from '@/core/event-bus';

/** 把 getUserMedia 的各种失败翻译成人话 */
function explainMediaError(err: unknown, what: '摄像头' | '麦克风'): RuntimeError {
  if (err instanceof DOMException) {
    switch (err.name) {
      case 'NotAllowedError':
      case 'SecurityError':
        return new RuntimeError(
          `你拒绝了${what}权限，或者当前环境不允许使用。请在浏览器地址栏的权限设置里放行后重试。`,
          'RuntimeError',
        );
      case 'NotFoundError':
      case 'OverconstrainedError':
        return new RuntimeError(`这台设备上找不到可用的${what}。`, 'RuntimeError');
      case 'NotReadableError':
        return new RuntimeError(`${what}被其他程序占用了，关掉正在使用它的应用再试。`, 'RuntimeError');
      default:
        return new RuntimeError(`${what}打开失败：${err.message}`, 'RuntimeError');
    }
  }
  return new RuntimeError(`${what}打开失败：${err instanceof Error ? err.message : String(err)}`);
}

/** 检查是否处于安全上下文 —— 这是很多设备 API 的硬性前提 */
function assertSecureContext(what: string): void {
  // localhost 也算安全上下文，isSecureContext 已经涵盖
  if (typeof window !== 'undefined' && 'isSecureContext' in window && !window.isSecureContext) {
    throw new RuntimeError(
      `${what}只能在 HTTPS 或 localhost 下使用。当前页面不是安全上下文，浏览器会直接拦掉。`,
      'RuntimeError',
    );
  }
}

/** 打开摄像头流。cameraOpen 与 cameraCapture 共用，避免后者去调前者而耦合方法表顺序 */
async function openCameraStream(): Promise<'opened' | 'already'> {
  assertSecureContext('摄像头');
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new RuntimeError('这个浏览器不支持摄像头调用。', 'RuntimeError');
  }
  if (deviceRuntime.isCameraOn) return 'already';

  let stream: MediaStream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
  } catch (err) {
    throw explainMediaError(err, '摄像头');
  }
  deviceRuntime.camera.stream = stream;
  eventBus.emit('device:camera-changed', { on: true });
  return 'opened';
}

export const deviceApi = {
  name: 'device',
  desc: '设备的真实硬件：摄像头、麦克风、剪贴板',
  userDesc: '调用真实硬件 —— 开摄像头拍照、开麦克风、读写剪贴板',
  methods: [
    /* ---------------- 摄像头 ---------------- */
    {
      name: 'cameraOpen',
      desc: '打开摄像头预览。用户说「打开摄像头」「拍一张」而预览还没开时先用这个。',
      params: [],
      successText: '已打开摄像头',
      requireConfirm: () => '应用要打开摄像头并显示实时画面，同意吗？',
      run: async () => {
        const r = await openCameraStream();
        if (r === 'already') return { text: '摄像头本来就是开着的' };
        return { value: { on: true } };
      },
    },
    {
      name: 'cameraCapture',
      desc: '拍一张照片。用户说「拍张照」「拍下来」时用这个。摄像头没开时会自动先打开。',
      params: [],
      successText: '已拍照',
      run: async () => {
        // 没开就先开 —— 用户说「拍一张」时不该先让他说「打开摄像头」
        await openCameraStream();
        const stream = deviceRuntime.camera.stream;
        if (!stream) throw new RuntimeError('摄像头没能打开，无法拍照。', 'RuntimeError');

        const video = document.createElement('video');
        video.srcObject = stream;
        video.muted = true;
        await video.play();

        const track = stream.getVideoTracks()[0];
        const settings = track?.getSettings?.() ?? {};
        const w = settings.width ?? video.videoWidth ?? 640;
        const h = settings.height ?? video.videoHeight ?? 480;

        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new RuntimeError('无法创建画布来保存照片。', 'RuntimeError');
        ctx.drawImage(video, 0, 0, w, h);

        const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
        video.pause();
        video.srcObject = null;

        if (!blob) throw new RuntimeError('照片生成失败。', 'RuntimeError');

        const name = `照片_${new Date().toLocaleString('zh-CN').replace(/[/: ]/g, '-')}.png`;
        deviceRuntime.camera.lastPhoto = blob;
        deviceRuntime.camera.lastPhotoName = name;

        // 登记句柄，让用户可以把这张照片发出去（如 file.sendPhoto）
        const handleId = handles.add('image', name, { blob, name });

        return { value: { handleId, name, size: blob.size } };
      },
    },
    {
      name: 'cameraClose',
      desc: '关闭摄像头预览。',
      params: [],
      successText: '已关闭摄像头',
      run: () => {
        deviceRuntime.camera.stream?.getTracks().forEach((t) => t.stop());
        deviceRuntime.camera.stream = null;
        eventBus.emit('device:camera-changed', { on: false });
        return {};
      },
    },

    /* ---------------- 麦克风 ---------------- */
    {
      name: 'micOpen',
      desc: '打开麦克风。用户说「打开麦克风」「我要录音」时用这个。',
      params: [],
      successText: '已打开麦克风',
      requireConfirm: () => '应用要使用你的麦克风，同意吗？',
      run: async () => {
        assertSecureContext('麦克风');
        if (!navigator.mediaDevices?.getUserMedia) {
          throw new RuntimeError('这个浏览器不支持麦克风调用。', 'RuntimeError');
        }
        if (deviceRuntime.isMicOn) return { text: '麦克风本来就是开着的' };
        let stream: MediaStream;
        try {
          stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
        } catch (err) {
          throw explainMediaError(err, '麦克风');
        }
        deviceRuntime.setMic(stream);
        return { value: { on: true } };
      },
    },
    {
      name: 'micClose',
      desc: '关闭麦克风。',
      params: [],
      successText: '已关闭麦克风',
      run: () => {
        deviceRuntime.setMic(null);
        return {};
      },
    },

    /* ---------------- 剪贴板 ---------------- */
    {
      name: 'copy',
      desc: '把一段文字复制到剪贴板。用户说「复制 xxx」「把 xxx 复制下来」时用这个。',
      params: [{ name: 'text', type: 'string' as const, desc: '要复制的文字', required: true }],
      successText: (args: unknown[]) => {
        const t = String(args[0]);
        const shown = t.length > 30 ? `${t.slice(0, 30)}…` : t;
        return `已复制到剪贴板：${shown}`;
      },
      run: async (args: unknown[]) => {
        const text = String(args[0]);
        if (!navigator.clipboard?.writeText) {
          throw new RuntimeError('这个浏览器不支持写入剪贴板。', 'RuntimeError');
        }
        try {
          await navigator.clipboard.writeText(text);
        } catch {
          throw new RuntimeError(
            '剪贴板写入被拒绝。浏览器通常要求这个操作由用户点击触发 —— 你是通过 AI 语音/文字发起的，可能被拦下。可以再点一次发送试试。',
            'RuntimeError',
          );
        }
        return { value: { length: text.length } };
      },
    },
    {
      name: 'paste',
      desc: '读取剪贴板里的文字。用户说「粘贴」「读一下剪贴板」时用这个。',
      params: [],
      successText: '已读取剪贴板',
      run: async () => {
        if (!navigator.clipboard?.readText) {
          throw new RuntimeError('这个浏览器不支持读取剪贴板。', 'RuntimeError');
        }
        try {
          const text = await navigator.clipboard.readText();
          return { value: text, text: text ? `剪贴板内容：${text.slice(0, 60)}` : '剪贴板是空的' };
        } catch {
          throw new RuntimeError('读取剪贴板被拒绝（通常需要用户手势触发）。', 'RuntimeError');
        }
      },
    },
  ],
} satisfies ObjectDef;
